import { useEffect, useState } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import type { FeeReceipt } from '@/types/fees';
import { feesApi } from '../api/fees.api';

export interface CancelReceiptDialogProps {
  /** The receipt being cancelled, or null while the dialog is closed. */
  receipt: FeeReceipt | null;
  /**
   * Gets the cancelled receipt back once the server has taken the money off.
   *
   * The confirmation is the caller's to raise, not this dialog's: on the day
   * book there is nothing left on screen and a toast is right, while inside the
   * account dialog the ledger itself redraws — struck through and labelled —
   * and a toast would sit *behind* the open `<dialog>`, in the browser's top
   * layer, where nobody would see it.
   */
  onCancelled: (receipt: FeeReceipt) => void;
  onDismiss: () => void;
}

/**
 * Cancelling a receipt — a bounced cheque, or money entered against the wrong
 * child.
 *
 * Its own dialog rather than a `ConfirmDialog`, because this one takes a
 * *reason* and the reason is the point. Money is coming back off a family's
 * account, and "who reversed this and why" is the first question asked
 * afterwards; a plain yes/no would leave the ledger unable to answer it.
 */
export function CancelReceiptDialog({
  receipt,
  onCancelled,
  onDismiss,
}: CancelReceiptDialogProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Cleared whenever the dialog opens, so one receipt's reason never appears
  // over the next.
  useEffect(() => {
    if (!receipt) return;
    setReason('');
    setError(null);
  }, [receipt]);

  async function confirm() {
    if (!receipt) return;

    const trimmed = reason.trim();
    if (trimmed.length < 4) {
      setError('Say why this receipt is being cancelled.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await feesApi.cancelPayment(receipt.uuid, trimmed);
      onCancelled(receipt);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={receipt !== null}
      onClose={onDismiss}
      size="sm"
      title={`Cancel receipt ${receipt?.receiptNumber ?? ''}`}
      description={
        receipt
          ? `${formatCurrency(receipt.amount)} taken on ${formatDate(receipt.paidOn)}.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onDismiss} disabled={isSaving}>
            Keep it
          </Button>
          <Button variant="danger" onClick={confirm} isLoading={isSaving}>
            {isSaving ? 'Cancelling…' : 'Cancel this receipt'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && (
          <Alert tone="critical" title="Could not cancel this receipt">
            {error}
          </Alert>
        )}

        <Alert tone="warning" title="The amount comes back off the account">
          Nothing is deleted. The receipt stays on the ledger marked cancelled, with your name and
          this reason against it.
        </Alert>

        <Textarea
          label="Why is it being cancelled?"
          rows={3}
          placeholder="Cheque returned unpaid, entered against the wrong student"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={200}
        />
      </div>
    </Modal>
  );
}
